import { useAuthStore } from '../store';

/**
 * Hook to check user permissions
 * Provides convenient methods to check user access permissions
 */
export const usePermission = () => {
  const permissions = useAuthStore((state) => state.permissions);

  /**
   * Check if user has permission to access a resource
   * Supports two formats:
   * 1. canAccess(PERMISSIONS.PRODUCT.INDEX) - with constant
   * 2. canAccess('product', 'index') - with separate params
   * 
   * @param resourceOrPermission - Resource name OR full permission string
   * @param action - Action type (optional if using full permission string)
   * @returns true if user has permission
   */
  const canAccess = (resourceOrPermission: string, action?: string): boolean => {
    let resource: string;
    let permAction: string;

    // If action is provided, treat as separate params
    if (action) {
      resource = resourceOrPermission;
      permAction = action;
    } else {
      // Otherwise, treat as full permission string (e.g., 'product.index')
      [resource, permAction] = resourceOrPermission.split('.');
      if (!permAction) permAction = 'index';
    }

    if (!resource || !permAction) {
      return false;
    }

    const resourcePermissions = permissions.find((item) => item.name === resource);
    if (!resourcePermissions) {
      return false;
    }

    return resourcePermissions.permissions[permAction] === true;
  };

  /**
   * Check if user has any of the specified permissions
   * @param checks - Array of [resource, action] tuples
   * @returns true if user has at least one permission
   * 
   * @example
   * hasAnyPermission([['product', 'create'], ['product', 'update']])
   */
  const hasAnyPermission = (checks: Array<[string, string]>): boolean => {
    return checks.some(([resource, action]) => canAccess(resource, action));
  };

  /**
   * Check if user has all of the specified permissions
   * @param checks - Array of [resource, action] tuples
   * @returns true if user has all permissions
   * 
   * @example
   * hasAllPermissions([['product', 'create'], ['product', 'update']])
   */
  const hasAllPermissions = (checks: Array<[string, string]>): boolean => {
    return checks.every(([resource, action]) => canAccess(resource, action));
  };

  return {
    canAccess,
    hasAnyPermission,
    hasAllPermissions,
    permissions,
  };
};
